import { TriangleAlert } from "lucide-react";
import React from "react";
import styles from "./PageNotFound.module.scss";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
  isChunkError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const message = error?.message || "";
    const isChunkError =
      /chunk|loading chunk|dynamically imported|failed to fetch|importing a module/i.test(
        message,
      );
    return { hasError: true, errorMessage: message, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Error caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorBoundaryContainer}>
          <h2>
            <TriangleAlert color="crimson" size={40} />
            {this.state.isChunkError
              ? "Page failed to load"
              : this.state.errorMessage
                ? ""
                : "Something went wrong"}
          </h2>
          <pre>
            {this.state.isChunkError
              ? "The page script did not finish downloading. This often happens on a slow connection."
              : this.state.errorMessage}
          </pre>

          <button
            onClick={() => {
              if (this.state.isChunkError) {
                window.location.reload();
                return;
              }
              this.setState({
                hasError: false,
                errorMessage: null,
                isChunkError: false,
              });
            }}
          >
            {this.state.isChunkError ? "Reload page" : "Try Again"}
          </button>
          <p>Please refresh or try again.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
